import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { User, At, CheckCircle, XCircle, Shield, Lock, Crown, ArrowRight, SignOut, Envelope } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { InstallAppButton } from "../components/InstallActions";
import { EPL_TEAMS } from "../lib/eplTeams";
import { PasswordInput } from "../components/PasswordInput";
import { UserAvatar } from "../components/UserAvatar";
import { PRESETS, DEFAULT_AVATAR } from "../lib/avatars";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [favouriteTeam, setFavouriteTeam] = useState(user?.favourite_team || "");
  const [avatar, setAvatar] = useState(user?.avatar || DEFAULT_AVATAR);
  const [available, setAvailable] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setUsername(user?.username || "");
    setFavouriteTeam(user?.favourite_team || "");
    setAvatar(user?.avatar || DEFAULT_AVATAR);
  }, [user]);

  useEffect(() => {
    if (!username || username === user?.username) {
      setAvailable(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/auth/username/available`, { params: { username } });
        setAvailable(data.available);
      } catch {
        setAvailable(null);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [username, user?.username]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put("/auth/profile", { name: name.trim(), username: username.trim(), favourite_team: favouriteTeam, avatar });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Account</span>
      <h1 className="font-heading text-5xl uppercase font-black mt-3 flex items-center gap-3"><User weight="duotone" /> Profile</h1>

      <MembershipCard user={user} />

      <form onSubmit={save} className="mt-10 surface border border-white/10 p-8 space-y-6" data-testid="profile-form">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">Icon</label>
          <div className="flex items-center gap-4">
            <UserAvatar avatar={avatar} size={64} border testid="profile-avatar-preview" />
            <div className="flex-1 text-xs text-white/40 uppercase tracking-widest">
              Pick from 30 presets · shown on leaderboard &amp; private leagues
            </div>
          </div>
          <div className="mt-4 grid grid-cols-6 sm:grid-cols-10 gap-2" data-testid="avatar-picker">
            {PRESETS.map((preset) => {
              const selected = preset === avatar;
              return (
                <button
                  key={preset}
                  type="button"
                  data-testid={`avatar-option-${preset}`}
                  onClick={() => setAvatar(preset)}
                  aria-pressed={selected}
                  className={`p-1 transition-all rounded-full ${selected
                    ? "ring-2 ring-primary-yellow ring-offset-2 ring-offset-[#0B0D11] scale-110"
                    : "opacity-60 hover:opacity-100 hover:scale-105"}`}
                >
                  <UserAvatar avatar={preset} size={36} />
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">Display name</label>
          <input
            data-testid="profile-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold flex items-center gap-2">
            <At size={12} /> Nickname
            <span className="text-white/30 normal-case tracking-normal">3–20 chars · letters, numbers, underscore</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 font-mono-num">@</span>
            <input
              data-testid="profile-username"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              pattern="[a-zA-Z0-9_]{3,20}"
              maxLength={20}
              className="w-full bg-[#0B0D11] border border-white/10 pl-8 pr-10 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num"
            />
            {available === true && <CheckCircle size={18} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-yellow" />}
            {available === false && <XCircle size={18} weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400" />}
          </div>
          {available === false && <p className="text-xs text-red-400 mt-2">That nickname is taken or invalid.</p>}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold flex items-center gap-2">
            <Shield size={12} /> Favourite Premier League team
            <span className="text-white/30 normal-case tracking-normal">(optional — shown on your profile)</span>
          </label>
          <select
            data-testid="profile-favourite-team"
            value={favouriteTeam}
            onChange={(e) => setFavouriteTeam(e.target.value)}
            className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow text-white"
          >
            <option value="">— None —</option>
            {EPL_TEAMS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Email</div>
            <div className="font-mono-num text-sm mt-1">{user?.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Total points</div>
            <div className="font-mono-num text-2xl font-black text-primary-yellow mt-1">{user?.total_points}</div>
          </div>
        </div>
        <button
          data-testid="profile-save"
          type="submit"
          disabled={saving || available === false}
          className="w-full bg-primary-yellow text-black uppercase tracking-[0.2em] font-bold py-4 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>

      <PasswordCard />

      <EmailPreferencesCard />

      <div className="mt-10 surface border border-white/10 p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-base uppercase font-black">Install on this device</h3>
          <p className="text-xs text-white/50 mt-1">One-tap access from your home screen. Works on iPhone &amp; Android.</p>
        </div>
        <InstallAppButton />
      </div>

      <div className="mt-10 surface border border-white/10 p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-base uppercase font-black flex items-center gap-2"><SignOut size={16} /> Sign out</h3>
          <p className="text-xs text-white/50 mt-1">End this session and return to the home page.</p>
        </div>
        <button
          data-testid="profile-logout"
          onClick={() => {
            if (!window.confirm("Sign out of PitchIQ?")) return;
            logout();
            navigate("/");
          }}
          className="border border-red-500/40 text-red-300 hover:bg-red-500/10 hover:border-red-500 uppercase tracking-[0.2em] font-bold px-6 py-3 text-xs flex items-center gap-2 transition-colors"
        >
          <SignOut size={14} weight="bold" /> Log Out
        </button>
      </div>
    </div>
  );
}

const MembershipCard = ({ user }) => {
  const isPremier = !!user?.is_premier;
  const expires = user?.premier_expires_at ? new Date(user.premier_expires_at) : null;
  const daysLeft = expires ? Math.max(0, Math.ceil((expires - new Date()) / (1000 * 60 * 60 * 24))) : null;

  if (isPremier) {
    const isAdmin = !!user?.is_admin;
    return (
      <div data-testid="membership-card-premier" className="mt-10 surface-elevated border border-primary-yellow/40 glow-yellow p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary-yellow font-bold">
              <Crown size={12} weight="fill" /> {isAdmin ? "Admin · Lifetime Access" : "Active Membership"}
            </div>
            <h2 className="font-heading text-3xl uppercase font-black mt-2 text-primary-yellow">You&apos;re Premier</h2>
            <p className="text-white/70 mt-2 text-sm">
              Full season fixtures, unlimited private leagues, detailed stats — all unlocked.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/40">{isAdmin ? "Plan" : "Expires"}</div>
            {isAdmin ? (
              <>
                <div className="font-heading text-2xl font-black mt-1 text-primary-yellow">Lifetime</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Never expires</div>
              </>
            ) : (
              <>
                <div className="font-mono-num text-2xl font-black mt-1">{expires ? expires.toLocaleDateString() : "—"}</div>
                {daysLeft !== null && (
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{daysLeft} days left</div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { v: "Full", l: "Season fixtures" },
            { v: "∞", l: "Private leagues" },
            { v: "PREM", l: "Leaderboard badge" },
          ].map((s) => (
            <div key={s.l} className="bg-[#0B0D11] py-4 px-3 text-center">
              <div className="font-mono-num text-2xl font-black text-primary-yellow leading-none">{s.v}</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mt-2 truncate">{s.l}</div>
            </div>
          ))}
        </div>
        {daysLeft !== null && daysLeft <= 30 && !isAdmin && (
          <Link
            data-testid="membership-renew-cta"
            to="/pricing"
            className="mt-6 w-full bg-primary-yellow text-black uppercase tracking-[0.22em] font-bold py-3 text-xs hover:-translate-y-0.5 transition-transform inline-flex items-center justify-center gap-2"
          >
            Renew now · £9.99 <ArrowRight size={12} weight="bold" />
          </Link>
        )}
      </div>
    );
  }

  return (
    <div data-testid="membership-card-free" className="mt-10 surface-elevated border border-primary-yellow/30 glow-yellow p-8 relative overflow-hidden">
      <span className="absolute -top-3 left-8 px-3 py-1 bg-primary-yellow text-black text-[10px] uppercase tracking-[0.25em] font-bold">
        Recommended
      </span>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">
            <User size={12} weight="fill" /> Free Basic
          </div>
          <h2 className="font-heading text-3xl uppercase font-black mt-2">Upgrade to <span className="text-primary-yellow">Premier</span></h2>
          <p className="text-white/70 mt-2 text-sm max-w-md">
            One-off £9.99 for the season. Full fixtures upfront, unlimited private leagues, detailed stats, Premier badge on every leaderboard.
          </p>
        </div>
        <div className="text-right">
          <div className="font-mono-num text-5xl font-black text-primary-yellow leading-none">£9.99</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">per season</div>
        </div>
      </div>
      <ul className="mt-6 space-y-2 text-sm">
        {[
          "See the full season's fixtures upfront",
          "Create unlimited private leagues",
          "Detailed stats page with charts",
          "Premier badge on your leaderboard row",
        ].map((f) => (
          <li key={f} className="flex items-start gap-2 text-white/80">
            <CheckCircle size={14} weight="fill" className="text-primary-yellow mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <Link
        data-testid="membership-upgrade-cta"
        to="/pricing"
        className="mt-6 w-full bg-primary-yellow text-black uppercase tracking-[0.22em] font-bold py-4 text-sm hover:-translate-y-0.5 transition-transform inline-flex items-center justify-center gap-2"
      >
        <Crown size={14} weight="fill" /> Upgrade to Premier <ArrowRight size={12} weight="bold" />
      </Link>
    </div>
  );
};

const PasswordCard = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await apiClient.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mt-10 surface border border-white/10 p-8 space-y-5" data-testid="password-form">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary-yellow font-bold">
        <Lock size={12} weight="fill" /> Security
      </div>
      <h2 className="font-heading text-2xl uppercase font-black">Change password</h2>
      <PwField label="Current password" testid="password-current" value={current} onChange={setCurrent} />
      <PwField label="New password" testid="password-new" value={next} onChange={setNext} hint="At least 6 characters" />
      <PwField label="Confirm new password" testid="password-confirm" value={confirm} onChange={setConfirm} />
      <button
        data-testid="password-save"
        type="submit"
        disabled={saving || !current || !next || !confirm}
        className="w-full bg-primary-yellow text-black uppercase tracking-[0.2em] font-bold py-4 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50"
      >
        {saving ? "Updating…" : "Update Password"}
      </button>
    </form>
  );
};

const PwField = ({ label, value, onChange, testid, hint }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">{label}</label>
    <PasswordInput
      testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="new-password"
    />
    {hint && <p className="text-[10px] text-white/30 mt-1 uppercase tracking-widest">{hint}</p>}
  </div>
);


const EMAIL_STREAMS = [
  {
    key: "prekickoff",
    title: "Pre-kickoff reminders",
    desc: "24 hours before each matchweek locks — a nudge listing the fixtures you still need to predict.",
  },
  {
    key: "matchweek_results",
    title: "Matchweek result digest",
    desc: "One email per matchweek recapping your points, exact scores, season total and global rank.",
  },
  {
    key: "rank_digest",
    title: "Weekly rank movement",
    desc: "Every Monday morning: how many places you climbed or slipped, plus the entrant just ahead of you.",
  },
];

const EmailPreferencesCard = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/me/email-preferences")
      .then(({ data }) => { if (!cancelled) setPrefs(data.email_prefs); })
      .catch(() => { if (!cancelled) setPrefs({}); });
    return () => { cancelled = true; };
  }, []);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = !prefs[key];
    setPrefs({ ...prefs, [key]: next });
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await apiClient.put("/me/email-preferences", { [key]: next });
      setPrefs(data.email_prefs);
      toast.success(next ? "Emails turned on" : "Emails turned off");
    } catch (err) {
      // roll back on failure
      setPrefs((p) => ({ ...p, [key]: !next }));
      toast.error(err?.response?.data?.detail || "Could not save preference");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <section className="mt-10 surface border border-white/10 p-8" data-testid="email-prefs-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary-yellow font-bold">
        <Envelope size={12} weight="fill" /> Notifications
      </div>
      <h2 className="font-heading text-2xl uppercase font-black mt-2">Email preferences</h2>
      <p className="text-xs text-white/50 mt-1">Toggle each stream on or off. Changes save instantly.</p>
      <div className="mt-6 divide-y divide-white/5 border border-white/10">
        {EMAIL_STREAMS.map((s) => {
          const on = prefs?.[s.key] ?? true;
          return (
            <div key={s.key} className="p-5 flex items-start justify-between gap-6">
              <div className="min-w-0">
                <h3 className="font-heading text-sm uppercase font-black">{s.title}</h3>
                <p className="text-xs text-white/50 mt-1 leading-relaxed">{s.desc}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                data-testid={`email-pref-toggle-${s.key}`}
                disabled={!prefs || saving[s.key]}
                onClick={() => toggle(s.key)}
                className={`relative shrink-0 h-7 w-12 rounded-full border transition-colors disabled:opacity-50 ${
                  on ? "bg-primary-yellow border-primary-yellow" : "bg-transparent border-white/25"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full transition-all ${
                    on ? "left-6 bg-black" : "left-0.5 bg-white/60"
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};
